/**
 * MetricStat - Displays a large number with a label below
 *
 * Atomic level: Molecule
 * Used in: CollectionHeader for displaying record counts and median severity
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../../lib/utils';

const metricStatVariants = cva('flex flex-col gap-1', {
  variants: {
    size: {
      sm: '',
      md: '',
      lg: '',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const valueVariants = cva('font-jakarta font-extrabold leading-tight tracking-[-0.03em] text-foreground', {
  variants: {
    size: {
      sm: 'text-2xl',
      md: 'text-4xl',
      lg: 'text-3xl',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const labelVariants = cva('uppercase tracking-[0.16em] text-muted-foreground', {
  variants: {
    size: {
      sm: 'text-[10px]',
      md: 'text-[10px]',
      lg: 'text-[10px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

interface MetricStatProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof metricStatVariants> {
  value: number | string;
  label: string;
}

const MetricStat = React.forwardRef<HTMLDivElement, MetricStatProps>(
  ({ className, size, value, label, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(metricStatVariants({ size }), className)}
      {...props}
    >
      <span className={valueVariants({ size })}>{value}</span>
      <span className={labelVariants({ size })}>{label}</span>
    </div>
  )
);

MetricStat.displayName = 'MetricStat';

export { MetricStat, metricStatVariants };
